import * as React from 'react';
import { debounce } from '../utils/debounce';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
/*
 * Returns the DOM measurements from a created ref.
 *
 * Example where contentRef is placed on the element's ref attribute:
 * const [contentRef, dimensions] = useDimensions()
 */
export function useDimensions() {
  const ref = React.useRef(null);
  const [dimensions, setDimensions] = React.useState({});
  const [forceUpdate, setForceUpdate] = React.useState(0);
  const forceUpdateHandler = () => {
    setForceUpdate(prev => prev + 1);
  };
  useIsomorphicLayoutEffect(() => {
    /*
     * In SSR environments, bail!
     * Note, returning `undefined` to prevent ESLInt from complaining about
     * "consistent-return" later on with the hook's returned cleanup function.
     */
    if (typeof window === 'undefined') return undefined;
    const calculateDimensions = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
          right: rect.right,
          bottom: rect.bottom,
          x: 'x' in rect ? rect.x : rect.left,
          y: 'y' in rect ? rect.y : rect.top
        });
      }
    };
    calculateDimensions();

    // Recalculates dimensions on resize
    const resizeHandler = debounce(() => calculateDimensions());
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [ref, forceUpdate]);
  return [ref, dimensions, forceUpdateHandler];
}